import { reactive } from 'vue';

/**
 * Tracks how the user reached the current route, so a preview landing on a
 * record can tell a back/forward traversal — where the browser already restored
 * the view the user asked for, tab and edit mode included — from a forward
 * navigation, where the record simply opens the way the org says records open.
 *
 * Read by BaseOverview (resume an interrupted inline edit) and by the sales and
 * purchases previews (never re-enter the editor the user just left).
 */
const state = reactive({ lastNavWasPop: false, previousRouteName: null });

let popNavigation = false;
let installed = false;

// Registered at import time — before createRouter() attaches Vue Router's own
// popstate listener — so this flag is set first and is already true when Vue
// Router synchronously runs the afterEach hook for a back/forward navigation.
// (routes.js imports this module before it creates the router.)
window.addEventListener('popstate', () => {
    popNavigation = true;
});

export function installModuleEntryTracker(router) {
    if (installed) {
        return;
    }
    installed = true;

    router.afterEach((to, from) => {
        state.lastNavWasPop = popNavigation;
        popNavigation = false;

        state.previousRouteName = from.name ?? null;
    });
}

/**
 * Whether the most recent navigation was a back/forward (popstate) traversal.
 * On such navigations the browser already restored the exact URL, so previews
 * must not override what it landed on.
 */
export function wasLastNavigationPop() {
    return state.lastNavWasPop;
}

/**
 * Name of the route the last navigation left. Read by previews that open their
 * editor on arrival (see `utils/openInEditMode.js`): the editor's Save and
 * Cancel both land back on the preview, and that return must not be mistaken
 * for a fresh opening.
 */
export function previousRouteName() {
    return state.previousRouteName;
}

// Which record (if any) is currently in inline-edit mode. Kept in memory (not
// the URL) so history.back can resume editing the exact record the user left,
// while a fresh link entry starts in read mode. Only one record edits at a time.
const editState = reactive({ key: null });

const editKey = (entityType, id) => `${entityType || ''}:${id}`;

export function markEditing(entityType, id, isEditing) {
    const key = editKey(entityType, id);
    if (isEditing) {
        editState.key = key;
    } else if (editState.key === key) {
        editState.key = null;
    }
}

/**
 * True when `id` of `entityType` is the record the user was editing, so a
 * back/forward navigation onto it should resume edit mode.
 */
export function shouldResumeEditing(entityType, id) {
    return editState.key !== null && editState.key === editKey(entityType, id);
}

export function forgetEditing() {
    editState.key = null;
}
